import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import styles from "./NuovaComunicazione.module.css";
import SelettoreDipendenti from "./SelettoreDipendenti";

import { API_BASE } from "../api";

const API = API_BASE;

export default function NuovaComunicazione() {
  const navigate = useNavigate();

  // form core
  const [titolo, setTitolo] = useState("");
  const [contenuto, setContenuto] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  // allegati multipli
  const [files, setFiles] = useState([]); // Array<File>

  // destinatari
  const [inviaATutti, setInviaATutti] = useState(true);

  // filtri
  const [societaList, setSocietaList] = useState([]);
  const [sediList, setSediList] = useState([]);

  const [selectedSocieta, setSelectedSocieta] = useState([]); // array di { id, label }
  const [selectedSedi, setSelectedSedi] = useState([]);       // array di stringhe nome

  const [sending, setSending] = useState(false);

  // ==== LOAD LOOKUPS ====
  useEffect(() => {
    Promise.all([
      fetch(`${API}/societa`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/sedi`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
    ])
    .then(([soc, sed]) => {
      setSocietaList(Array.isArray(soc) ? soc : []);
      const arr = Array.isArray(sed) ? sed : (sed?.items || []);
      setSediList(arr.map(s => s?.nome || s).filter(Boolean));
    })
    .catch(() => {});
  }, []);

  const handleAddSocieta = (id, label) => {
    if (!id || selectedSocieta.some(s => String(s.id) === String(id))) return;
    setSelectedSocieta(prev => [...prev, { id, label }]);
  };

  const handleRemoveSocieta = (id) => {
    setSelectedSocieta(prev => prev.filter(s => String(s.id) !== String(id)));
  };

  const handleAddSede = (nome) => {
    if (!nome || selectedSedi.includes(nome)) return;
    setSelectedSedi(prev => [...prev, nome]);
  };

  const handleRemoveSede = (nome) => {
    setSelectedSedi(prev => prev.filter(s => s !== nome));
  };

  const canSubmit = useMemo(() => {
    if (!titolo.trim() || !contenuto.trim()) return false;
    if (!inviaATutti && manualMode && selectedIds.length === 0) return false;
    return true;
  }, [titolo, contenuto, inviaATutti, manualMode, selectedIds]);

  const onFilesChange = (e) => {
    const list = Array.from(e.target.files || []);
    // concat per consentire più selezioni consecutive
    setFiles(prev => [...prev, ...list]);
    // reset del valore per riattivare lo stesso file se riselezionato
    e.target.value = "";
  };

  const removeFileAt = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSending(true);
    try {
      const fd = new FormData();
      fd.append("titolo", titolo);
      fd.append("contenuto", contenuto);
      fd.append("send_email", sendEmail ? "1" : "0");

      // allegati multipli => stesso field name ripetuto (multer li raccoglie)
      for (const f of files) {
        fd.append("allegato", f);
      }

      // destinatari
      fd.append("invia_a_tutti", inviaATutti ? "1" : "0");
      if (!inviaATutti) {
        if (manualMode) {
          fd.append("utente_ids", JSON.stringify(selectedIds));
        } else {
          if (selectedSocieta.length) fd.append("societa_ids", JSON.stringify(selectedSocieta.map(s => s.id)));
          if (selectedSedi.length) fd.append("sede_ids", JSON.stringify(selectedSedi));
        }
      }

      const res = await fetch(`${API}/comunicazioni`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }

      alert("Comunicazione creata");
      navigate("/comunicazioni");
    } catch (err) {
      alert(err?.message || "Errore invio comunicazione");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Nuova comunicazione</h2>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate("/comunicazioni")}
        >
          <FiArrowLeft /> Torna all’elenco
        </button>
      </div>
      <div className={styles.underline} />

      <form className={styles.card} onSubmit={handleSubmit}>
        {/* Titolo + Allegati */}
        <div className={styles.grid}>
          <div className={styles.group}>
            <label className={styles.label} htmlFor="titolo">Titolo</label>
            <input
              id="titolo"
              type="text"
              className={styles.input}
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              placeholder="Inserisci il titolo"
              required
            />
          </div>

          <div className={styles.group} style={{ gridColumn: "1 / -1" }}>
            <label className={styles.label} htmlFor="contenuto">Contenuto</label>
            <textarea
              id="contenuto"
              className={styles.textarea}
              value={contenuto}
              onChange={(e) => setContenuto(e.target.value)}
              placeholder="Scrivi il testo della comunicazione…"
              required
            />
          </div>
        </div>

        <div className={styles.group}>
          <label className={styles.label} htmlFor="file">Allegati (opzionale)</label>

          {/* CONTROLLO FILE CUSTOM: input nascosto + bottone + box nomi */}
          <input
            id="file"
            type="file"
            className={styles.fileHidden}
            accept="application/pdf,image/*"
            multiple
            onChange={onFilesChange}
          />

          <div className={styles.fileControl}>
            <button
              type="button"
              className={styles.fileBtn}
              onClick={() => document.getElementById("file")?.click()}
            >
              Scegli file
            </button>

            <div
              className={styles.fileBox}
              onClick={() => document.getElementById("file")?.click()}
              role="button"
              tabIndex={0}
            >
              {files.length === 0 ? (
                <span className={styles.filePlaceholder}>Nessun file selezionato</span>
              ) : (
                <div className={styles.fileList}>
                  {files.map((f, i) => (
                    <span key={`${f.name}-${i}`} className={styles.fileChip}>
                      {f.name}
                      <button
                        type="button"
                        className={styles.removeChip}
                        onClick={(e) => { e.stopPropagation(); removeFileAt(i); }}
                        aria-label={`Rimuovi ${f.name}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Destinatari */}
        <div className={styles.grid}>
          <div className={styles.group} style={{ gridColumn: "1 / -1" }}>
            <label className={styles.label}>Destinatari</label>
            <div className={styles.inline}>
              <input
                id="inviaATutti"
                type="checkbox"
                checked={inviaATutti}
                onChange={(e) => { setInviaATutti(e.target.checked); if (e.target.checked) setManualMode(false); }}
              />
              <label htmlFor="inviaATutti" className={styles.note}>
                Invia a tutti gli utenti attivi
              </label>
            </div>
          </div>
          {!inviaATutti && (
            <div className={styles.inline} style={{ marginTop: 8 }}>
              <input
                id="manualMode"
                type="checkbox"
                checked={manualMode}
                onChange={(e) => {
                  const v = e.target.checked;
                  setManualMode(v);
                  if (v) { setSelectedSocieta([]); setSelectedSedi([]); }
                }}
              />
              <label htmlFor="manualMode" className={styles.note}>
                Seleziona manualmente i dipendenti
              </label>

              {manualMode && (
                <button
                  type="button"
                  className={styles.btnOutline}
                  onClick={() => setShowPicker(true)}
                  style={{ marginLeft: 12 }}
                >
                  Scegli dipendenti ({selectedIds.length})
                </button>
              )}
            </div>
          )}

          {!inviaATutti && !manualMode && (
            <>
              {/* Società multi-select */}
              <div className={styles.group}>
                <label className={styles.label}>Società</label>
                <select
                  className={styles.select}
                  value=""
                  onChange={(e) => {
                    const opt = e.target.options[e.target.selectedIndex];
                    handleAddSocieta(e.target.value, opt.text);
                    e.target.value = "";
                  }}
                >
                  <option value="">Aggiungi società…</option>
                  {societaList.map(s => (
                    <option key={s.id} value={s.id}>{s.ragione_sociale || s.nome || s.denominazione}</option>
                  ))}
                </select>
                <div className={styles.chipsWrap}>
                  {selectedSocieta.length === 0 && <span className={styles.note}>Nessuna (tutte)</span>}
                  {selectedSocieta.map(s => (
                    <span key={s.id} className={styles.filterChip}>
                      {s.label}
                      <button type="button" className={styles.filterChipRemove} onClick={() => handleRemoveSocieta(s.id)}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Sedi multi-select */}
              <div className={styles.group}>
                <label className={styles.label}>Sedi</label>
                <select
                  className={styles.select}
                  value=""
                  onChange={(e) => {
                    handleAddSede(e.target.value);
                    e.target.value = "";
                  }}
                >
                  <option value="">Aggiungi sede…</option>
                  {sediList.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <div className={styles.chipsWrap}>
                  {selectedSedi.length === 0 && <span className={styles.note}>Nessuna (tutte)</span>}
                  {selectedSedi.map(s => (
                    <span key={s} className={styles.filterChip}>
                      {s}
                      <button type="button" className={styles.filterChipRemove} onClick={() => handleRemoveSede(s)}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.group} style={{ gridColumn: "1 / -1" }}>
          <label className={styles.label}>Notifiche</label>
          <div className={styles.inline}>
            <input
              id="sendEmail"
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <label htmlFor="sendEmail" className={styles.note}>
              Invia anche via email
            </label>
          </div>
        </div>

        {/* Azioni */}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnOutline}
            onClick={() => navigate("/comunicazioni")}
          >
            Annulla
          </button>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={!canSubmit || sending}
          >
            {sending ? "Invio..." : "Invia comunicazione"}
          </button>
        </div>
      </form>
      {showPicker && (
        <SelettoreDipendenti
          allowMultiple={true}
          preselectedIds={selectedIds}
          onClose={() => setShowPicker(false)}
          onConfirm={(ids) => {
            setSelectedIds(ids || []);
            setShowPicker(false);
          }}
        />
      )}
    </div>
  );
}
